import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

const clamp = (v) => {
  const n = typeof v === "string" ? parseInt(v, 10) : v;
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(999, n);
};

const GiftSeatsPanel = ({ companyId, company, onRefreshCompany }) => {
  const [adminGift, setAdminGift] = useState(0);
  const [endUserGift, setEndUserGift] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const setGiftSeatLimits = useMemo(
    () => httpsCallable(functions, "setGiftSeatLimits"),
    []
  );

  useEffect(() => {
    const g = company?.giftSeatLimits || {};
    setAdminGift(clamp(g.admin ?? 0));
    setEndUserGift(clamp(g.endUser ?? 0));
  }, [company]);

  const save = async () => {
    if (!companyId) return;

    setSaving(true);
    setMsg("");

    try {
      await setGiftSeatLimits({
        companyId,
        adminGift: clamp(adminGift),
        endUserGift: clamp(endUserGift),
      });

      setMsg("Saved gift seats.");
      await onRefreshCompany?.();
    } catch (e) {
      console.error("GiftSeatsPanel save error:", e);
      setMsg(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Gift seats (no billing impact)</h3>
          <p className="text-xs text-slate-500 mt-1">
            These add to availability, but Stripe billing stays the same.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-[11px] text-slate-500">Gift admin seats</div>
          <input
            type="number"
            min={0}
            value={adminGift}
            onChange={(e) => setAdminGift(clamp(e.target.value))}
            className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            disabled={saving}
          />
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-[11px] text-slate-500">Gift end-user seats</div>
          <input
            type="number"
            min={0}
            value={endUserGift}
            onChange={(e) => setEndUserGift(clamp(e.target.value))}
            className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            disabled={saving}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">{msg}</div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save gift seats"}
        </button>
      </div>
    </div>
  );
};

export default GiftSeatsPanel;
